import type { Patient } from '@medplum/fhirtypes';
import React, { useMemo, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@eluve/components';
import {
  FhirEditScope,
  FhirResourceScope,
  type FhirWriteTransport,
  RenderResource,
  buildResolutionMap,
  formatHumanName,
  isPatientEditValid,
  useFhirEditScope,
  useFhirEditState,
} from '@eluve/fhir-renderer';
import { useTranslation } from '@eluve/i18n';
import { useMedplum } from '@eluve/medplum-client';

import { renderWithPrivateName } from './privacyName';

/**
 * The profile's "Edit profile" dialog: the renderer's Patient edit variant
 * inside its own edit scope. Save is gated on dirtiness and the required
 * identity fields; any dismissal with unsaved edits asks for confirmation
 * (there is deliberately no X affordance — Cancel is the way out).
 */

function patientDisplayName(patient: Patient): string {
  const official = patient.name?.find((name) => name.use === 'official');
  return formatHumanName(official ?? patient.name?.[0]);
}

const DialogBody: React.FC<{
  patient: Patient;
  onSaved: () => void;
  onClose: () => void;
}> = ({ patient, onSaved, onClose }) => {
  const { t } = useTranslation();
  const { dirtyCount, commit, reset } = useFhirEditScope();
  const { current } = useFhirEditState(patient);
  const [saving, setSaving] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const toast = useToast();
  const map = useMemo(() => buildResolutionMap([patient]), [patient]);

  const close = () => {
    setConfirmingDiscard(false);
    reset();
    onClose();
  };

  const requestClose = () => {
    if (dirtyCount > 0) setConfirmingDiscard(true);
    else close();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await commit();
      if (result.ok) {
        toast.success(t('patients.editProfile.updated'));
        onSaved();
        onClose();
      } else if (result.failures.some((f) => f.error.kind === 'conflict')) {
        toast.error(t('patients.editProfile.conflict'));
      } else {
        toast.error(
          t('patients.editProfile.saveFailed', {
            message:
              result.failures[0]?.error.message ??
              t('patients.editProfile.unknownError'),
          }),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent
      size="lg"
      showCloseButton={false}
      onEscapeKeyDown={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onInteractOutside={(event) => {
        event.preventDefault();
        requestClose();
      }}
    >
      <DialogHeader>
        <DialogTitle>{t('patients.rail.editProfile')}</DialogTitle>
        <DialogDescription>
          {renderWithPrivateName(
            t('patients.editProfile.description'),
            patientDisplayName(patient) ||
              t('patients.editProfile.thisPatient'),
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="-mx-1 max-h-[min(60vh,40rem)] scroll-fade overflow-y-auto p-1">
        <FhirResourceScope map={map} placement="modal">
          <RenderResource resource={patient} />
        </FhirResourceScope>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={requestClose}>
          {t('common.cancel')}
        </Button>
        <Button
          disabled={dirtyCount === 0 || !isPatientEditValid(current) || saving}
          onClick={() => void handleSave()}
          data-testid="save-patient-profile"
        >
          {saving
            ? t('patients.common.saving')
            : t('patients.common.saveChanges')}
        </Button>
      </DialogFooter>

      <AlertDialog open={confirmingDiscard} onOpenChange={setConfirmingDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('patients.editProfile.discardTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('patients.editProfile.discardDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('patients.common.keepEditing')}
            </AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={close}>
              {t('patients.editProfile.discardChanges')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContent>
  );
};

export const EditPatientDialog: React.FC<{
  patient: Patient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  /** Overridable for stories/tests; defaults to the Medplum SDK client. */
  transport?: FhirWriteTransport;
}> = ({ patient, open, onOpenChange, onSaved, transport }) => {
  const { client } = useMedplum();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closes route through the body's guarded requestClose only.
        if (next) onOpenChange(true);
      }}
    >
      {open && (
        <FhirEditScope active transport={transport ?? client ?? undefined}>
          <DialogBody
            patient={patient}
            onSaved={onSaved}
            onClose={() => onOpenChange(false)}
          />
        </FhirEditScope>
      )}
    </Dialog>
  );
};
